import { useState, useRef, useEffect, useCallback } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Radio,
  Plus,
  X,
  Copy,
  Check,
  Eye,
  EyeOff,
  Settings,
  Upload,
  Trash2,
  Play,
  Square,
  Activity,
  Captions,
  CaptionsOff,
  Clapperboard,
} from 'lucide-react';
import shaka from 'shaka-player/dist/shaka-player.experimental.debug';
import { cn } from '@/lib/utils';
import { apiAuthRequestFilter } from '@/lib/shakaApiAuth';
import { sourcesApi, segmentsApi, getAccessToken } from '@/api/client';
import { RoleGate } from '@/components/auth/RoleGate';
import { originHref, originPath, useProductionIndex, type SourceOrigin } from '@/hooks/useProductionIndex';
import { SOURCE_OPERATOR_ROLE } from '@jeket-cloud/types/auth';
import type { Source, SourceType, SourceStatus, SegmentReference } from '@/types';

// ---- Status Badge ----

function StatusBadge({ status }: { status: SourceStatus }) {
  const colors: Record<SourceStatus, string> = {
    idle: 'bg-silver-muted/20 text-silver-muted',
    active: 'bg-status-healthy/20 text-status-healthy',
    error: 'bg-status-error/20 text-status-error',
    retired: 'bg-surface-elevated text-silver-muted/70',
  };

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium', colors[status])}>
      {status}
    </span>
  );
}

// ---- Type Badge ----

function TypeBadge({ type }: { type: SourceType }) {
  const colors: Record<SourceType, string> = {
    cmaf: 'bg-gold/20 text-gold',
    moq: 'bg-status-info/20 text-blue-400',
  };

  return (
    <span className={cn('px-2.5 py-0.5 rounded-full text-xs font-medium uppercase', colors[type])}>
      {type}
    </span>
  );
}

// ---- Copy Button ----

function CopyButton({ text, className }: { text: string; className?: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    await navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <button
      onClick={handleCopy}
      className={cn(
        'p-1 rounded text-silver-muted hover:text-silver-bright transition-colors',
        className
      )}
      title="Copy to clipboard"
    >
      {copied ? <Check size={14} className="text-status-healthy" /> : <Copy size={14} />}
    </button>
  );
}

// ---- Create Source Dialog ----

interface CreateSourceDialogProps {
  open: boolean;
  onClose: () => void;
}

function CreateSourceDialog({ open, onClose }: CreateSourceDialogProps) {
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [type, setType] = useState<SourceType>('cmaf');

  const createMutation = useMutation({
    mutationFn: () =>
      sourcesApi.create({
        name,
        description: description || undefined,
        type,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      resetAndClose();
    },
  });

  function resetAndClose() {
    setName('');
    setDescription('');
    setType('cmaf');
    onClose();
  }

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
      <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-lg mx-4 shadow-2xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-surface-elevated">
          <h3 className="text-lg font-semibold text-silver-bright">Create Source</h3>
          <button onClick={resetAndClose} className="text-silver-muted hover:text-silver-bright">
            <X size={18} />
          </button>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            createMutation.mutate();
          }}
          className="p-6 space-y-4"
        >
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Name *</label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="My Source"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Description</label>
            <input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright placeholder-silver-muted/50 focus:outline-none focus:border-gold/50"
              placeholder="Source description"
            />
          </div>
          <div>
            <label className="block text-xs font-medium text-silver-muted mb-1.5">Type *</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value as SourceType)}
              className="w-full bg-surface-deep border border-surface-elevated rounded-lg px-3 py-2 text-sm text-silver-bright focus:outline-none focus:border-gold/50"
            >
              <option value="cmaf">CMAF</option>
              <option value="moq">MOQ</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={resetAndClose}
              className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={!name || createMutation.isPending}
              className="px-4 py-2 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {createMutation.isPending ? 'Creating...' : 'Create Source'}
            </button>
          </div>
          {createMutation.isError && (
            <p className="text-xs text-status-error">
              {createMutation.error instanceof Error ? createMutation.error.message : 'Failed to create source'}
            </p>
          )}
        </form>
      </div>
    </div>
  );
}

// ---- Info Row ----

function InfoRow({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-xs text-silver-muted shrink-0">{label}</span>
      <span
        className={cn(
          'text-sm text-silver-bright text-right break-all',
          mono && 'font-mono text-xs'
        )}
      >
        {value || '--'}
      </span>
    </div>
  );
}

// ---- Source Preview ----

const MOQ_RELAY_URL = import.meta.env.VITE_MOQ_RELAY_URL ?? 'https://relay.jeket.com:4443/moq-relay';

function SourcePreview({ sourceId, sourceType }: { sourceId: string; sourceType: SourceType }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const playerRef = useRef<shaka.Player | null>(null);
  const [playing, setPlaying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasCaptions, setHasCaptions] = useState(false);
  const [captionsOn, setCaptionsOn] = useState(false);

  const destroy = useCallback(async () => {
    if (videoRef.current && (videoRef.current as any).__moqDiagCleanup) {
      (videoRef.current as any).__moqDiagCleanup();
      delete (videoRef.current as any).__moqDiagCleanup;
    }
    if (playerRef.current) {
      await playerRef.current.destroy();
      playerRef.current = null;
    }
    setPlaying(false);
    setError(null);
    setHasCaptions(false);
    setCaptionsOn(false);
  }, []);

  const toggleCaptions = useCallback(() => {
    const player = playerRef.current;
    if (!player) return;
    const next = !captionsOn;
    try {
      const tracks = player.getTextTracks();
      // Shaka 5.x: selectTextTrack(track) shows captions, null hides them.
      if (next && tracks[0]) player.selectTextTrack(tracks[0]);
      else if (!next) player.selectTextTrack(null);
      setCaptionsOn(next);
    } catch (err) {
      console.error('[Preview] Failed to toggle captions:', err);
    }
  }, [captionsOn]);

  // Poll for a caption track once playing (text tracks appear after Shaka
  // builds the segment index).
  useEffect(() => {
    if (!playing) return;
    const check = () => {
      const player = playerRef.current;
      if (!player) return;
      try {
        setHasCaptions(player.getTextTracks().length > 0);
      } catch {
        setHasCaptions(false);
      }
    };
    check();
    const interval = setInterval(check, 1_000);
    return () => clearInterval(interval);
  }, [playing]);

  useEffect(() => {
    return () => { destroy(); };
  }, [destroy]);

  const handlePlay = async () => {
    if (playing) {
      await destroy();
      return;
    }

    const video = videoRef.current;
    if (!video) return;

    setError(null);

    shaka.polyfill.installAll();
    if (!shaka.Player.isBrowserSupported()) {
      setError('Browser not supported for Shaka Player');
      return;
    }

    const player = new shaka.Player();
    // The HLS preview's manifests and segments under /api/ go through the
    // BFF, which needs the bearer; Shaka fetches them itself (JSS#1015).
    player.getNetworkingEngine()?.registerRequestFilter(apiAuthRequestFilter);
    await (player as any).attach(video);
    playerRef.current = player;

    player.addEventListener('error', (event: any) => {
      const detail = event.detail;
      const videoErr = video.error;
      console.error('[MOQ Preview] Playback error:', detail);
      console.error('[MOQ Preview] Shaka error code:', detail?.code, 'category:', detail?.category, 'data:', JSON.stringify(detail?.data));
      if (videoErr) {
        console.error('[MOQ Preview] MediaError code:', videoErr.code, 'message:', videoErr.message);
      }
      setError(`Playback error: ${detail.message ?? detail.code}`);
      setPlaying(false);
    });

    const loadStart = performance.now();
    try {
      if (sourceType === 'moq') {
        const token = getAccessToken();
        const moqUrl = token
          ? `${MOQ_RELAY_URL}/${sourceId}?token=${token}`
          : `${MOQ_RELAY_URL}/${sourceId}`;
        console.log('[MOQ Preview] Connecting to relay:', moqUrl);
        console.log('[MOQ Preview] Namespace:', sourceId);
        console.log('[MOQ Preview] Load start:', loadStart.toFixed(0), 'ms');
        (player as any).configure({
          manifest: { msf: { namespaces: [sourceId] } },
          streaming: {
            lowLatencyMode: false,
            rebufferingGoal: 0.5,
            bufferingGoal: 4,
            bufferBehind: 10,
            updateIntervalSeconds: 0.25,
            safeSeekOffset: 2,
            retryParameters: { baseDelay: 200, maxAttempts: 5 },
          },
        });
        await (player as any).load(moqUrl, undefined, 'application/msf');
        console.log('[MOQ Preview] Loaded successfully after', (performance.now() - loadStart).toFixed(0), 'ms');

        const manifest = (player as any).getManifest?.();
        if (manifest?.presentationTimeline) {
          const timeline = manifest.presentationTimeline;
          const oldDuration = timeline.getSegmentAvailabilityDuration?.();
          timeline.setSegmentAvailabilityDuration(30);
          console.log('[MOQ Preview] Fixed segmentAvailabilityDuration:', oldDuration, '→ 30s');
          console.log('[MOQ Preview] Seek range:', timeline.getSeekRangeStart?.()?.toFixed(3), '-', timeline.getSeekRangeEnd?.()?.toFixed(3));
        }
      } else {
        await player.load(`/api/manifests/${sourceId}/master.m3u8`);
      }
      video.muted = true;
      await video.play();
      setPlaying(true);

      if (sourceType === 'moq') {
        const diagInterval = setInterval(() => {
          if (!videoRef.current || !playerRef.current) {
            clearInterval(diagInterval);
            return;
          }
          const v = videoRef.current;
          const bufferedRanges: string[] = [];
          for (let i = 0; i < v.buffered.length; i++) {
            bufferedRanges.push(`[${v.buffered.start(i).toFixed(3)}-${v.buffered.end(i).toFixed(3)}]`);
          }
          const stats = (playerRef.current as any).getStats?.();
          const m = (playerRef.current as any).getManifest?.();
          const tl = m?.presentationTimeline;
          const seekInfo = tl ? `seek=[${tl.getSeekRangeStart?.()?.toFixed(3)}-${tl.getSeekRangeEnd?.()?.toFixed(3)}] avail=${tl.getSegmentAvailabilityDuration?.()?.toFixed(1)}s` : '';
          console.log(
            `[MOQ Diag] time=${v.currentTime.toFixed(3)} ready=${v.readyState} paused=${v.paused} ` +
            `ended=${v.ended} networkState=${v.networkState} ` +
            `buffered=${bufferedRanges.join(',') || 'none'} ` +
            `videoW=${v.videoWidth} videoH=${v.videoHeight} ` +
            `decodedFrames=${(v as any).webkitDecodedFrameCount ?? 'N/A'} ` +
            `droppedFrames=${(v as any).webkitDroppedFrameCount ?? 'N/A'} ` +
            seekInfo +
            (stats ? ` stats={w:${stats.width},h:${stats.height},buffering:${stats.bufferingTime?.toFixed(1)},stalls:${stats.stallsDetected}}` : '')
          );
        }, 1000);
        const logEvent = (e: Event) => console.log(`[MOQ Diag] video event: ${e.type}`);
        for (const evt of ['waiting', 'stalled', 'suspend', 'error', 'playing', 'pause', 'seeking', 'seeked', 'canplay', 'canplaythrough'] as const) {
          video.addEventListener(evt, logEvent);
        }
        (video as any).__moqDiagCleanup = () => {
          clearInterval(diagInterval);
          for (const evt of ['waiting', 'stalled', 'suspend', 'error', 'playing', 'pause', 'seeking', 'seeked', 'canplay', 'canplaythrough'] as const) {
            video.removeEventListener(evt, logEvent);
          }
        };
      }
    } catch (err: any) {
      const elapsed = (performance.now() - loadStart).toFixed(0);
      console.error('[MOQ Preview] Failed after', elapsed, 'ms — code:', err?.code, 'category:', err?.category, 'severity:', err?.severity);
      console.error('[MOQ Preview] Full error:', JSON.stringify(err, null, 2));
      console.error('[MOQ Preview] Error object:', err);
      setError(err.message ?? 'Failed to load stream');
      await destroy();
    }
  };

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Play size={16} className="text-gold" />
          <h3 className="text-sm font-semibold text-silver-bright">Preview</h3>
        </div>
        <div className="flex items-center gap-2">
          {playing && hasCaptions && (
            <button
              onClick={toggleCaptions}
              className={cn(
                'p-1.5 rounded-lg transition-colors',
                captionsOn
                  ? 'bg-gold/20 text-gold hover:bg-gold/30'
                  : 'text-silver-muted hover:text-silver-bright'
              )}
              title={captionsOn ? 'Turn captions off' : 'Turn captions on'}
              aria-pressed={captionsOn}
            >
              {captionsOn ? <Captions size={14} /> : <CaptionsOff size={14} />}
            </button>
          )}
          <button
            onClick={handlePlay}
            className={cn(
              'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg transition-colors',
              playing
                ? 'bg-status-error/20 text-status-error hover:bg-status-error/30'
                : 'bg-gold/20 text-gold hover:bg-gold/30'
            )}
          >
            {playing ? <><Square size={12} /> Stop</> : <><Play size={12} /> Play Preview</>}
          </button>
        </div>
      </div>
      <div className="relative aspect-video bg-surface-deep rounded-lg overflow-hidden">
        <video
          ref={videoRef}
          className="w-full h-full object-contain"
          controls
          playsInline
          muted
        />
        {!playing && !error && (
          <div className="absolute inset-0 flex items-center justify-center text-silver-muted text-sm">
            Click "Play Preview" to start
          </div>
        )}
      </div>
      {error && (
        <p className="mt-2 text-xs text-status-error">{error}</p>
      )}
    </div>
  );
}

// ---- Stream Activity ----

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function formatAge(timestamp: string): string {
  const age = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (age < 60) return `${age}s ago`;
  return `${Math.floor(age / 60)}m ${age % 60}s ago`;
}

function StreamActivity({ sourceId, active }: { sourceId: string; active: boolean }) {
  const { data: state } = useQuery({
    queryKey: ['segments', sourceId],
    queryFn: () => segmentsApi.getState(sourceId),
    refetchInterval: active ? 2_000 : false,
    enabled: active,
  });

  const segments = state?.segments ?? [];
  const totalBytes = segments.reduce((sum, s) => sum + s.byteSize, 0);
  const avgDuration = segments.length > 0
    ? Math.round(segments.reduce((sum, s) => sum + s.durationMs, 0) / segments.length)
    : 0;
  const latest = segments.length > 0 ? segments[segments.length - 1] : null;

  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <Activity size={16} className="text-gold" />
        <h3 className="text-sm font-semibold text-silver-bright">Stream Activity</h3>
        {active && (
          <span className="ml-auto flex items-center gap-1.5 text-xs text-status-healthy">
            <span className="w-1.5 h-1.5 rounded-full bg-status-healthy animate-pulse" />
            Live
          </span>
        )}
      </div>

      {!active && (
        <p className="text-sm text-silver-muted">Source is not active. Start streaming to see activity.</p>
      )}

      {active && (
        <>
          <div className="grid grid-cols-2 gap-3 mb-4">
            <div className="bg-surface-deep rounded-lg p-3">
              <p className="text-xs text-silver-muted">Segments</p>
              <p className="text-lg font-semibold text-silver-bright">{state?.currentSegmentSequence ?? 0}</p>
            </div>
            <div className="bg-surface-deep rounded-lg p-3">
              <p className="text-xs text-silver-muted">Latest</p>
              <p className="text-sm font-medium text-silver-bright truncate">
                {latest ? formatAge(latest.timestamp) : '--'}
              </p>
            </div>
            <div className="bg-surface-deep rounded-lg p-3">
              <p className="text-xs text-silver-muted">Avg Duration</p>
              <p className="text-sm font-medium text-silver-bright">
                {avgDuration ? `${(avgDuration / 1000).toFixed(1)}s` : '--'}
              </p>
            </div>
            <div className="bg-surface-deep rounded-lg p-3">
              <p className="text-xs text-silver-muted">Total Bytes</p>
              <p className="text-sm font-medium text-silver-bright">
                {totalBytes ? formatBytes(totalBytes) : '--'}
              </p>
            </div>
          </div>

          {segments.length > 0 && (
            <div className="max-h-48 overflow-y-auto rounded-lg border border-surface-elevated">
              <table className="w-full text-xs">
                <thead className="bg-surface-deep sticky top-0">
                  <tr className="text-silver-muted">
                    <th className="text-left px-3 py-2 font-medium">Seq</th>
                    <th className="text-left px-3 py-2 font-medium">Track</th>
                    <th className="text-right px-3 py-2 font-medium">Size</th>
                    <th className="text-right px-3 py-2 font-medium">Age</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-surface-elevated">
                  {[...segments].reverse().map((seg) => (
                    <tr key={seg.sequence} className="text-silver-bright hover:bg-surface-elevated/50">
                      <td className="px-3 py-1.5 font-mono">{seg.sequence}</td>
                      <td className="px-3 py-1.5">{seg.trackName}</td>
                      <td className="px-3 py-1.5 text-right font-mono">{formatBytes(seg.byteSize)}</td>
                      <td className="px-3 py-1.5 text-right text-silver-muted">{formatAge(seg.timestamp)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// ---- Production origin (JSS#1161) ----

/** The JBS production output a source is published by, linking to the Production page. */
function SourceOriginCard({ origin }: { origin: SourceOrigin }) {
  return (
    <div className="bg-surface border border-surface-elevated rounded-xl p-5" data-testid="source-origin">
      <div className="flex items-center gap-2 mb-3">
        <Clapperboard size={16} className="text-gold" />
        <h3 className="text-sm font-semibold text-silver-bright">Production</h3>
        {origin.onAir && <span className="text-xs font-semibold text-status-error">On air</span>}
      </div>
      <p className="text-sm text-silver-bright">{originPath(origin)}</p>
      <p className="text-xs text-silver-muted mt-1">
        {origin.siteName} ·{' '}
        <Link to={originHref(origin)} className="text-gold hover:underline">
          View in Production
        </Link>
      </p>
    </div>
  );
}

// ---- Source Detail Panel ----

function SourceDetailPanel({
  sourceId,
  onClose,
  origin,
}: {
  sourceId: string;
  onClose: () => void;
  origin?: SourceOrigin;
}) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showKey, setShowKey] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const { data: source, isLoading, error } = useQuery({
    queryKey: ['source', sourceId],
    queryFn: () => sourcesApi.get(sourceId),
    refetchInterval: 5_000,
  });

  const deleteMutation = useMutation({
    mutationFn: () => sourcesApi.delete(sourceId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sources'] });
      navigate('/streaming/sources');
    },
  });

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64 text-silver-muted text-sm">
        Loading source...
      </div>
    );
  }

  if (error || !source) {
    return (
      <div className="flex items-center justify-center h-64 text-status-error text-sm">
        Failed to load source
      </div>
    );
  }

  const authHeader = source.streamKey
    ? `Basic ${btoa(`source:${source.streamKey}`)}`
    : '';

  return (
    <div className="h-full overflow-y-auto">
      {/* Panel Header */}
      <div className="sticky top-0 z-10 bg-surface-deep border-b border-surface-elevated px-5 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-surface-elevated flex items-center justify-center shrink-0">
            <Radio size={16} className="text-gold" />
          </div>
          <div className="min-w-0">
            <h2 className="text-base font-bold text-silver-bright truncate">{source.name}</h2>
            <p className="text-xs text-silver-muted font-mono truncate">{source.id}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <TypeBadge type={source.type} />
          <StatusBadge status={source.status} />
          <button
            onClick={onClose}
            className="ml-2 p-1 rounded text-silver-muted hover:text-silver-bright transition-colors"
          >
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="p-5 space-y-5">
        {source.status === 'retired' && (
          <div className="rounded-xl border border-surface-elevated bg-surface px-4 py-3 text-sm text-silver-muted">
            Retired{source.retiredAt ? ` ${new Date(source.retiredAt).toLocaleString()}` : ''}: no AirChain on
            its site uses this source any more, so it cannot be published to. It comes back if the site's
            JBS activates an AirChain that uses it.
          </div>
        )}
        {/* Source Info */}
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Settings size={16} className="text-gold" />
            <h3 className="text-sm font-semibold text-silver-bright">Source Info</h3>
          </div>
          {source.description && (
            <p className="text-sm text-silver-muted mb-4">{source.description}</p>
          )}
          <div className="space-y-3">
            <InfoRow label="Type" value={source.type.toUpperCase()} />
            <InfoRow label="Status" value={source.status} />
            <InfoRow label="Created" value={source.createdAt ? new Date(source.createdAt).toLocaleString() : ''} />
            <InfoRow label="Updated" value={source.updatedAt ? new Date(source.updatedAt).toLocaleString() : ''} />
            <InfoRow
              label="Segment Duration"
              value={source.config?.segmentDurationMs ? `${source.config.segmentDurationMs}ms` : '--'}
            />
          </div>
        </div>

        {origin && <SourceOriginCard origin={origin} />}

        {/* Ingest Panel */}
        <div className="bg-surface border border-surface-elevated rounded-xl p-5">
          <div className="flex items-center gap-2 mb-4">
            <Upload size={16} className="text-gold" />
            <h3 className="text-sm font-semibold text-silver-bright">Ingest</h3>
          </div>

          <div className="space-y-4">
            {/* Ingest URL */}
            <div>
              <h4 className="text-xs font-medium text-silver-muted mb-2">Ingest URL</h4>
              <div className="flex items-center gap-2 bg-surface-deep rounded-lg px-3 py-2">
                <code className="text-xs text-silver-bright font-mono break-all flex-1">
                  {source.ingestUrl ?? '--'}
                </code>
                {source.ingestUrl && (
                  <CopyButton text={source.ingestUrl} />
                )}
              </div>
            </div>

            {/* Stream key and the header built from it: a source operator's
                only, as channel-service sends the key only to one (JSS#1001).
                No ingest validates either today. */}
            <RoleGate role={SOURCE_OPERATOR_ROLE}>
              <div>
                <h4 className="text-xs font-medium text-silver-muted mb-2">Stream Key</h4>
                <div className="flex items-center gap-2 bg-surface-deep rounded-lg px-3 py-2">
                  <code className="text-xs text-silver-bright font-mono break-all flex-1">
                    {source.streamKey
                      ? showKey
                        ? source.streamKey
                        : '\u2022'.repeat(24)
                      : '--'}
                  </code>
                  {source.streamKey && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button
                        onClick={() => setShowKey(!showKey)}
                        className="p-1 rounded text-silver-muted hover:text-silver-bright transition-colors"
                        title={showKey ? 'Hide stream key' : 'Reveal stream key'}
                      >
                        {showKey ? <EyeOff size={14} /> : <Eye size={14} />}
                      </button>
                      <CopyButton text={source.streamKey} />
                    </div>
                  )}
                </div>
              </div>

              {/* Auth Header */}
              {authHeader && (
                <div>
                  <h4 className="text-xs font-medium text-silver-muted mb-2">Auth Header</h4>
                  <div className="flex items-center gap-2 bg-surface-deep rounded-lg px-3 py-2">
                    <code className="text-xs text-silver-bright font-mono break-all flex-1">
                      {authHeader}
                    </code>
                    <CopyButton text={authHeader} />
                  </div>
                </div>
              )}
            </RoleGate>
          </div>
        </div>

        {/* Preview (active sources only) */}
        {source.status === 'active' && (
          <SourcePreview sourceId={sourceId} sourceType={source.type} />
        )}

        {/* Stream Activity */}
        <StreamActivity sourceId={sourceId} active={source.status === 'active'} />

        {/* Delete: DELETE /api/sources/:id is a source operator's (JSS#1001) */}
        <RoleGate role={SOURCE_OPERATOR_ROLE}>
          <div className="bg-surface border border-status-error/20 rounded-xl p-5">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-status-error">Danger Zone</h3>
                <p className="text-xs text-silver-muted mt-1">Permanently delete this source</p>
              </div>
              <button
                onClick={() => setConfirmDelete(true)}
                disabled={deleteMutation.isPending}
                className="flex items-center gap-1.5 px-4 py-2 bg-status-error/20 text-status-error text-sm font-medium rounded-lg hover:bg-status-error/30 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <Trash2 size={14} />
                Delete
              </button>
            </div>
          </div>
        </RoleGate>
      </div>

      {/* Delete Confirmation Modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
          <div className="bg-surface border border-surface-elevated rounded-xl w-full max-w-sm mx-4 shadow-2xl p-6 space-y-4">
            <h3 className="text-lg font-semibold text-silver-bright">Delete Source</h3>
            <p className="text-sm text-silver-muted">
              Are you sure you want to delete <span className="text-silver-bright font-medium">{source.name}</span>? This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={() => setConfirmDelete(false)}
                className="px-4 py-2 text-sm text-silver-muted hover:text-silver-bright transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => { setConfirmDelete(false); deleteMutation.mutate(); }}
                className="px-4 py-2 bg-status-error text-white text-sm font-medium rounded-lg hover:bg-status-error/80 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---- Main Export ----

export default function Sources() {
  const { sourceId } = useParams<{ sourceId: string }>();
  const navigate = useNavigate();
  const [createOpen, setCreateOpen] = useState(false);

  // Retired sources (JSS#1151: device sources no AirChain on their site
  // references any more) are hidden unless asked for. They are never deleted.
  const [showRetired, setShowRetired] = useState(false);

  const { data: allSources, isLoading, error } = useQuery({
    queryKey: ['sources'],
    queryFn: sourcesApi.list,
    refetchInterval: 10_000,
  });
  // Which JBS production output publishes each source. A site whose read
  // fails resolves nothing: its sources render as before.
  const { index: origins } = useProductionIndex();
  const retiredCount = allSources?.filter((s) => s.status === 'retired').length ?? 0;
  const sources = showRetired ? allSources : allSources?.filter((s) => s.status !== 'retired');

  return (
    <div className="flex h-[calc(100vh-3.5rem)] overflow-hidden">
      {/* Left: Source List */}
      <div className={cn(
        'border-r border-surface-elevated flex flex-col shrink-0 overflow-hidden',
        sourceId ? 'w-80' : 'w-full max-w-2xl'
      )}>
        {/* List Header */}
        <div className="px-4 py-4 border-b border-surface-elevated flex items-center justify-between shrink-0">
          <div>
            <h2 className="text-lg font-bold text-silver-bright">Sources</h2>
            <p className="text-xs text-silver-muted mt-0.5">
              {sources ? `${sources.length} source${sources.length !== 1 ? 's' : ''}` : 'Loading...'}
            </p>
            {retiredCount > 0 && (
              <label className="mt-1 flex items-center gap-1.5 text-xs text-silver-muted cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showRetired}
                  onChange={(e) => setShowRetired(e.target.checked)}
                  className="accent-gold"
                />
                Show retired ({retiredCount})
              </label>
            )}
          </div>
          {/* POST /api/sources is a source operator's (JSS#1001) */}
          <RoleGate role={SOURCE_OPERATOR_ROLE}>
            <button
              onClick={() => setCreateOpen(true)}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-gold text-surface-deep text-sm font-medium rounded-lg hover:bg-gold-light transition-colors"
            >
              <Plus size={14} />
              New
            </button>
          </RoleGate>
        </div>

        {/* Source Items */}
        <div className="flex-1 overflow-y-auto">
          {isLoading && (
            <div className="text-center py-12 text-silver-muted text-sm">Loading sources...</div>
          )}
          {error && !sources && (
            <div className="text-center py-12 text-status-error text-sm">Failed to load sources</div>
          )}
          {sources && sources.length === 0 && retiredCount > 0 && !showRetired && (
            <div className="text-center py-16 text-silver-muted text-sm">
              No sources in use. {retiredCount} retired source{retiredCount !== 1 ? 's are' : ' is'} hidden.
            </div>
          )}
          {sources && sources.length === 0 && (retiredCount === 0 || showRetired) && (
            <div className="text-center py-16 space-y-3">
              <Radio size={40} className="mx-auto text-silver-muted/30" />
              <p className="text-silver-muted">No sources yet</p>
              <RoleGate role={SOURCE_OPERATOR_ROLE}>
                <button
                  onClick={() => setCreateOpen(true)}
                  className="text-gold text-sm hover:text-gold-light transition-colors"
                >
                  Create your first source
                </button>
              </RoleGate>
            </div>
          )}

          {sources?.map((source) => {
            const origin = origins.get(source.id);
            return (
            <button
              key={source.id}
              onClick={() => navigate(`/streaming/sources/${source.id}`)}
              className={cn(
                'w-full text-left px-4 py-3 border-b border-surface-elevated transition-colors',
                sourceId === source.id
                  ? 'bg-gold/10 border-l-2 border-l-gold'
                  : 'hover:bg-surface-elevated/50'
              )}
            >
              <div className="flex items-center justify-between mb-1">
                <span className={cn(
                  'text-sm font-medium truncate',
                  sourceId === source.id ? 'text-gold' : 'text-silver-bright'
                )}>
                  {source.name}
                </span>
                <StatusBadge status={source.status} />
              </div>
              <div className="flex items-center gap-2">
                <TypeBadge type={source.type} />
                {source.description && (
                  <span className="text-xs text-silver-muted truncate">{source.description}</span>
                )}
              </div>
              {origin && (
                <div
                  className="flex items-center gap-1.5 mt-1 text-xs text-silver-muted min-w-0"
                  data-testid={`source-origin-${source.id}`}
                >
                  <Clapperboard size={12} className="shrink-0 text-gold" />
                  <span className="truncate" title={`${originPath(origin)} (${origin.siteName})`}>
                    {originPath(origin)}
                    <span className="text-silver-muted/70"> · {origin.siteName}</span>
                  </span>
                </div>
              )}
            </button>
            );
          })}
        </div>
      </div>

      {/* Right: Detail Panel */}
      {sourceId ? (
        <div className="flex-1 overflow-hidden bg-surface-deep">
          <SourceDetailPanel
            key={sourceId}
            sourceId={sourceId}
            origin={origins.get(sourceId)}
            onClose={() => navigate('/streaming/sources')}
          />
        </div>
      ) : (
        <div className="flex-1 hidden lg:flex items-center justify-center text-silver-muted">
          <div className="text-center space-y-2">
            <Radio size={48} className="mx-auto text-silver-muted/20" />
            <p className="text-sm">Select a source to view details</p>
          </div>
        </div>
      )}

      <CreateSourceDialog open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}
